class NavBlog extends HTMLElement {
  constructor() {
    super();

    this.blogUrl = this.dataset.blogUrl || '';
    this.isTagged = this.dataset.isTagged === 'true';
    this.tag = (this.dataset.tag || '').toLowerCase();

    this.loading = false;
    this.abortController = new AbortController();

    this.searchInput = this.querySelector('#BlogSearch');
    this.form = this.searchInput?.closest('form');
    this.submitButton = this.querySelector('.blog__submit');

    this.grid = document.querySelector('#blog-grid');

    this.resultsContainer = document.querySelector('.blog__results');
    this.resultsList = this.resultsContainer?.querySelector('.blog__results-list');
    this.noResultsEl = this.resultsContainer?.querySelector('.blog__no-results');
    this.message = this.querySelector('.blog__message');

    if (!this.searchInput || !this.grid || !this.resultsContainer || !this.resultsList) {
      console.warn('[NavBlog] Required DOM elements not found');
      return;
    }

    this.#init();
  }

  #init() {
    this.#initSubmit();
    this.#showBrowse();
  }

  #initSubmit() {
    if (!this.form) return;

    this.form.addEventListener('submit', e => {
      e.preventDefault();
      e.stopPropagation();

      this.#onSearch(e);
    });
  }

  async #onSearch(e) {
    const query = this.searchInput.value.toLowerCase().trim();

    this.#isShowMessage(query.length < 3);

    if (query.length < 3 || this.loading) {
      this.#resetResults();
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    this.#setLoading(true);

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'article',
      'resources[limit]': '12',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'body,tag,title',
    });

    try {
      const res = await fetch(
        `${window.routes.predictive_search_url}.json?${params.toString()}`,
        { signal: this.abortController.signal }
      );

      if (!res.ok) throw new Error(res.status);

      const json = await res.json();
      let articles = json?.resources?.results?.articles || [];

      articles = articles.filter((a) =>
        a.url.includes(this.blogUrl)
      );

      if (this.isTagged && this.tag) {
        articles = this.#filterByTag(articles);
      }

      await this.#renderArticles(articles);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
      }
    } finally {
      this.#setLoading(false);
    }
  }

  #isShowMessage(isShow) {
    if (isShow) {
      this.message.classList.add('blog__message--active');
    } else {
      this.message.classList.remove('blog__message--active');
    }
  }

  async #renderArticles(articles) {
    this.resultsList.innerHTML = '';
    this.noResultsEl.classList.remove('blog__no-results--active');
    this.#showResults();

    if (!articles.length) {
      this.noResultsEl.classList.add('blog__no-results--active');
      this.#scrollToResults();

      return;
    }

    const requests = articles.map((article) =>
      this.#getArticleHTML(article.url)
    );
    const htmlChunks = await Promise.all(requests);

    htmlChunks.forEach((html) => {
      if (!html) return;

      const wrapper = document.createElement('div');

      wrapper.innerHTML = html;

      const item = wrapper.querySelector('.blog__article');

      if (item) this.resultsList.append(item);
    });

    this.#scrollToResults();
  }

  #scrollToResults() {
    this.resultsContainer.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  async #getArticleHTML(rawUrl) {
    const viewUrl = this.#getCleanArticleViewUrl(rawUrl);

    try {
      const res = await fetch(viewUrl);

      if (!res.ok) throw new Error(res.status);

      return await res.text();
    } catch (err) {
      console.error('Article HTML error:', err);
      return '';
    }
  }

  #getCleanArticleViewUrl(rawUrl) {
    const url = new URL(rawUrl, window.location.origin);

    url.search = '';
    url.searchParams.set('view', 'blog-card');

    return url.toString();
  }

  #showResults() {
    this.grid.classList.add('blog-post__results--hide');
    this.resultsContainer.classList.add('blog-post__results--active');
  }

  #showBrowse() {
    this.grid.classList.remove('blog-post__results--hide');
    this.resultsContainer.classList.remove('blog-post__results--active');
  }

  #resetResults() {
    this.resultsList.innerHTML = '';
    this.noResultsEl.classList.remove('blog-post__results--active');
    this.#showBrowse();
  }

  #filterByTag(items) {
    return items.filter(
      (item) =>
        Array.isArray(item.tags) &&
        item.tags.some(tag =>
          this.#handleize(tag) === this.tag
        )
    );
  }

  #handleize(str) {
    return String(str)
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #setLoading(state) {
    this.loading = state;

    if (!this.submitButton) return;

    this.submitButton.classList.toggle('loading', state);
  }
}

customElements.define('nav-blog', NavBlog);
